import { FC } from 'react';
import { Box, TableCell, TableRow, Typography } from '@mui/material';
import ListTable from '../../components/ListTable';
import { ListCard, ListCardField, ListCards } from '../../components/ListCards';
import { colors } from '../../theme/tokens';
import UserRowActions, {
  USER_ACTIONS_WIDTH,
  UserAction,
  UserName,
} from './UserRowActions';
import { UserRow, roleLabel } from './types';

// The table of XD screen 8 ("8. Liste users" in DESIGN.md), on
// desktop and tablet, and its cards on mobile (ROADMAP.md, Part 2).
//
// Columns: `Prénom NOM` · `Services` · `Rôle` · `Dernière connexion`.
//
// "Dernière connexion": `last_login_at`, which the login writes since
// `ac614a0`; `formatLastLogin` (types.ts) paints it like the XD, and "—" while
// the account has never logged in. `createdAt` is not used in its place.

export const COLUMNS = [
  'Prénom NOM',
  'Services',
  'Rôle',
  'Dernière connexion',
] as const;

export const USERS_ERROR_TEXT = 'Impossible de charger les utilisateurs.';

export const emptyUsersText = (hasFilters: boolean): string =>
  hasFilters
    ? 'Aucun utilisateur ne correspond à votre recherche.'
    : 'Aucun utilisateur pour le moment.';

export type UserResolvers = {
  serviceNames: (user: UserRow) => string;
  formatLastLogin: (user: UserRow) => string;
  canDelete: (user: UserRow) => boolean;
};

type Props = {
  users: UserRow[];
  isLoading: boolean;
  isError: boolean;
  hasFilters: boolean;
  resolvers: UserResolvers;
  onAction: (action: UserAction, user: UserRow) => void;
  onRetry: () => void;
};

export const UsersTable: FC<Props> = ({
  users,
  isLoading,
  isError,
  hasFilters,
  resolvers,
  onAction,
  onRetry,
}) => (
  <ListTable
    ariaLabel="Utilisateurs"
    columns={COLUMNS}
    actionsWidth={USER_ACTIONS_WIDTH}
    minWidth={640}
    isLoading={isLoading}
    isError={isError}
    isEmpty={users.length === 0}
    errorText={USERS_ERROR_TEXT}
    emptyText={emptyUsersText(hasFilters)}
    onRetry={onRetry}
  >
    {users.map((user) => (
      <TableRow key={user.id}>
        <TableCell>
          <UserName user={user} />
        </TableCell>
        <TableCell>{resolvers.serviceNames(user)}</TableCell>
        <TableCell>{roleLabel(user.role)}</TableCell>
        <TableCell>{resolvers.formatLastLogin(user)}</TableCell>
        <TableCell align="right">
          <UserRowActions
            user={user}
            canDelete={resolvers.canDelete(user)}
            variant="table"
            onAction={onAction}
          />
        </TableCell>
      </TableRow>
    ))}
  </ListTable>
);

// Mobile: name as title, services on the secondary line, role and last
// login labelled, and the "•••" menu at the bottom right.
export const UserCards: FC<Props> = ({
  users,
  isLoading,
  isError,
  hasFilters,
  resolvers,
  onAction,
  onRetry,
}) => (
  <ListCards
    ariaLabel="Utilisateurs"
    isLoading={isLoading}
    isError={isError}
    isEmpty={users.length === 0}
    errorText={USERS_ERROR_TEXT}
    emptyText={emptyUsersText(hasFilters)}
    onRetry={onRetry}
  >
    {users.map((user) => (
      <ListCard key={user.id}>
        <UserName user={user} />

        <Typography sx={{ fontSize: '0.875rem', color: colors.muted }}>
          {resolvers.serviceNames(user)}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 3 }}>
            <ListCardField label="Rôle">{roleLabel(user.role)}</ListCardField>
            <ListCardField label="Dernière connexion">
              {resolvers.formatLastLogin(user)}
            </ListCardField>
          </Box>
          <UserRowActions
            user={user}
            canDelete={resolvers.canDelete(user)}
            variant="card"
            onAction={onAction}
          />
        </Box>
      </ListCard>
    ))}
  </ListCards>
);
